import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { 
  getQuotations, 
  deleteQuotation, 
  updateQuotationStatus, 
  getQuotationStats,
  convertToInvoice,
  convertToSalesOrder
} from '../../services/api';

const Quotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [status, setStatus] = useState('');
  const [convertType, setConvertType] = useState('');
  const navigate = useNavigate();

  const statusColors = {
    Draft: 'default',
    Sent: 'primary',
    Approved: 'success',
    Rejected: 'error',
    Expired: 'warning',
    Converted: 'info'
  };

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const [quotationsResponse, statsResponse] = await Promise.all([
        getQuotations(),
        getQuotationStats()
      ]);
      
      if (quotationsResponse.success) {
        setQuotations(quotationsResponse.data);
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch quotations');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  const handleDeleteClick = (quotation) => {
    setSelectedQuotation(quotation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteQuotation(selectedQuotation.id);
      if (response.success) {
        await fetchQuotations();
        setDeleteDialogOpen(false);
        setSelectedQuotation(null);
      }
    } catch (err) {
      console.error('Error deleting quotation:', err);
    }
  };

  const handleStatusClick = (quotation) => {
    setSelectedQuotation(quotation);
    setStatus(quotation.status);
    setStatusDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    try {
      const response = await updateQuotationStatus(selectedQuotation.id, status);
      if (response.success) {
        await fetchQuotations();
        setStatusDialogOpen(false);
        setSelectedQuotation(null);
        setStatus('');
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleConvertClick = (quotation, type) => {
    setSelectedQuotation(quotation);
    setConvertType(type);
    setConvertDialogOpen(true);
  };

  const handleConvertConfirm = async () => {
    try {
      let response;
      if (convertType === 'invoice') {
        response = await convertToInvoice(selectedQuotation.id);
      } else {
        response = await convertToSalesOrder(selectedQuotation.id);
      }
      
      if (response.success) {
        await fetchQuotations();
        setConvertDialogOpen(false);
        setSelectedQuotation(null);
        setConvertType('');
        
        // Navigate to the converted document
        if (convertType === 'invoice') {
          navigate(`/invoices`);
        } else {
          navigate(`/sales`);
        }
      }
    } catch (err) {
      console.error('Error converting quotation:', err);
    }
  };

  const handleCreateQuotation = () => {
    navigate('/quotations/new');
  };

  const handleViewQuotation = (id) => {
    navigate(`/quotations/${id}`);
  };

  const handleEditQuotation = (id) => {
    navigate(`/quotations/edit/${id}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-LK');
  };

  const isQuotationExpired = (expiryDate) => {
    return new Date(expiryDate) < new Date();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Quotation Management
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchQuotations}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateQuotation}
          >
            New Quotation
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Quotes
              </Typography>
              <Typography variant="h5" component="div">
                {stats.totalQuotations || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Value
              </Typography>
              <Typography variant="h5" component="div">
                {stats.totalValue || 'LKR 0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Quotes
              </Typography>
              <Typography variant="h5" component="div">
                {stats.pendingQuotations || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Approved Quotes
              </Typography>
              <Typography variant="h5" component="div">
                {stats.approvedQuotations || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Quotations Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Quote #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No quotations found
                  </TableCell>
                </TableRow>
              ) : (
                quotations.map((quotation) => {
                  const isExpired = isQuotationExpired(quotation.expiry_date);
                  const showConvertButtons = !quotation.converted_to_invoice && 
                                           !quotation.converted_to_sales_order && 
                                           quotation.status === 'Approved';
                  
                  return (
                    <TableRow key={quotation.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {quotation.quotation_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {quotation.Customer?.name || 'N/A'}
                      </TableCell>
                      <TableCell>{formatDate(quotation.quotation_date)}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {formatDate(quotation.expiry_date)}
                          {isExpired && quotation.status !== 'Expired' && (
                            <Chip label="Expired" size="small" color="warning" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{quotation.total_amount}</TableCell>
                      <TableCell>
                        <Chip
                          label={quotation.status}
                          color={statusColors[quotation.status]}
                          size="small"
                          onClick={() => handleStatusClick(quotation)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleViewQuotation(quotation.id)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditQuotation(quotation.id)}
                              disabled={quotation.status === 'Converted' || quotation.status === 'Approved'}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(quotation)}
                              disabled={quotation.status === 'Converted' || quotation.status === 'Approved'}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                          
                          {showConvertButtons && (
                            <>
                              <Tooltip title="Convert to Invoice">
                                <IconButton
                                  size="small"
                                  onClick={() => handleConvertClick(quotation, 'invoice')}
                                  color="primary"
                                >
                                  <ReceiptIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Convert to Sales Order">
                                <IconButton
                                  size="small"
                                  onClick={() => handleConvertClick(quotation, 'sales')}
                                  color="secondary"
                                >
                                  <ShoppingCartIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete quotation {selectedQuotation?.quotation_number}?
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Update Quotation Status</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            margin="normal"
          >
            <MenuItem value="Draft">Draft</MenuItem>
            <MenuItem value="Sent">Sent</MenuItem>
            <MenuItem value="Approved">Approved</MenuItem>
            <MenuItem value="Rejected">Rejected</MenuItem>
            <MenuItem value="Expired">Expired</MenuItem>
            <MenuItem value="Converted">Converted</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)}>
        <DialogTitle>Convert Quotation</DialogTitle>
        <DialogContent>
          Are you sure you want to convert quotation {selectedQuotation?.quotation_number} to {convertType === 'invoice' ? 'Invoice' : 'Sales Order'}?
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConvertConfirm} variant="contained" color="primary">
            Convert
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Quotations;